import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Show, useIsMobile } from '@side/pantry';
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEffectOnce, useIntersection } from 'react-use';
import { useHasAccessToFeature } from '@/common/hooks/useHasAccessToFeature';
import { AppFeature } from '@/common/hooks/useHasAccessToFeature/types';
import { useSubmitAsTeamMembers } from '@/common/hooks/useSubmitAsTeamMembers';
import { InteractionType } from '@/common/types/interaction';
import { formatTeamMemberId } from '@/common/utils/formatTeamMemberId';
import {
  FactsAndFiguresContext,
  PieChart,
  TransactionStatusSnapshotChips,
  TransactionStatusSnapshotFilters,
  TransactionStatusSnapshotModal,
} from '@/views/Home/components/FactsAndFigures/components';
import { SectionContainer } from '@/views/Home/components/SectionContainer';
import { useFactsAndFigures } from '@/views/Home/hooks/useFactsAndFigures';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { getDateFilterCount } from './components/DatesFilter.helpers';
import { DATE_RANGE_OPTIONS, type SubmitAsOption } from './types';

export function FactsAndFigures() {
  const [agents, setAgents] = useState<SubmitAsOption[]>([]);
  const [dateRange, setDateRange] = useState<DATE_RANGE_OPTIONS>(
    DATE_RANGE_OPTIONS.DEFAULT,
  );
  const [endDate, setEndDate] = useState<Date | string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | string | null>(null);
  const sectionHeader = useRef<HTMLElement | null>(null);
  const [hasSeenSectionHeader, setHasSeenSectionHeader] = useState(false);
  const entry = useIntersection(sectionHeader as RefObject<HTMLElement>, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });

  useEffect(() => {
    if (!hasSeenSectionHeader && entry?.isIntersecting) {
      setHasSeenSectionHeader(true);
    }
  }, [entry?.isIntersecting, hasSeenSectionHeader, setHasSeenSectionHeader]);

  const { data: submitAsMembers = [], loading: isLoadingAgents } =
    useSubmitAsTeamMembers({
      skip: !hasSeenSectionHeader,
    });

  const trackEvent = useTrackHomePageAnalytics();

  const agentOptions = useMemo(
    () =>
      submitAsMembers
        .map((member) => ({
          label: member.displayName,
          value: formatTeamMemberId(member.id, member.teamId),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [submitAsMembers],
  );

  const selectedAgentsIds = useMemo(() => {
    if (!agents.length) return [];

    return agents.map(({ value }) => {
      return value;
    });
  }, [agents]);

  const hasLeasesAccess = useHasAccessToFeature(AppFeature.LEASES);
  const { data, loading: isLoading } = useFactsAndFigures({
    agents: selectedAgentsIds,
    endDate,
    startDate,
    dateRange,
    includeLeases: hasLeasesAccess,
  });

  const onClearAgents = useCallback(() => {
    setAgents([]);
  }, []);

  const onDeleteAgent = useCallback(
    (option: SubmitAsOption) => {
      setAgents((prevOptions) => {
        return prevOptions.filter((prevOption) => {
          return prevOption.value !== option.value;
        });
      });
    },
    [setAgents],
  );

  const isMobile = useIsMobile();

  const filtersCount =
    agents.length + getDateFilterCount({ startDate, endDate, dateRange });

  const toggleModal = useCallback(() => {
    setIsModalOpen((isOpen) => !isOpen);
  }, []);

  const onChangeAgents = useCallback(
    (newAgents: SubmitAsOption[]) => {
      setAgents([...newAgents].sort((a, b) => a.label.localeCompare(b.label)));
    },
    [setAgents],
  );

  const onChangeDateRange = useCallback(
    (newDateRange: DATE_RANGE_OPTIONS) => {
      setDateRange(newDateRange);
    },
    [setDateRange],
  );

  const onChangeEndDate = useCallback(
    (date: Date | string | null) => {
      setEndDate(date);
    },
    [setEndDate],
  );

  const onChangeStartDate = useCallback(
    (date: Date | string | null) => {
      setStartDate(date);
    },
    [setStartDate],
  );

  const onReset = useCallback(() => {
    if (isMobile) {
      trackEvent(TrackHomePageEvent.FactsAndFiguresMobileFilterClearAllClick);
    }

    onClearAgents();
    setDateRange(DATE_RANGE_OPTIONS.DEFAULT);
    setEndDate(null);
    setStartDate(null);
  }, [
    isMobile,
    onClearAgents,
    setDateRange,
    setEndDate,
    setStartDate,
    trackEvent,
  ]);

  const contextValue = useMemo(
    () => ({
      agentOptions,
      agents,
      dateRange,
      endDate,
      filtersCount,
      isLoadingAgents,
      isMobile,
      isModalOpen,
      startDate,
      onChangeAgents,
      onChangeDateRange,
      onChangeEndDate,
      onChangeStartDate,
      onClearAgents,
      onDeleteAgent,
      onReset,
      toggleModal,
    }),
    [
      agentOptions,
      agents,
      dateRange,
      endDate,
      filtersCount,
      isLoadingAgents,
      isMobile,
      isModalOpen,
      startDate,
      onChangeAgents,
      onChangeDateRange,
      onChangeEndDate,
      onChangeStartDate,
      onClearAgents,
      onDeleteAgent,
      onReset,
      toggleModal,
    ],
  );

  useEffectOnce(() => {
    trackEvent(TrackHomePageEvent.FactsAndFiguresMount);
  });

  return (
    <FactsAndFiguresContext.Provider value={contextValue}>
      <SectionContainer
        sx={{
          backgroundColor: 'background.default',
          pb: { xs: 10, md: 8 },
          pt: { xs: 2, md: 3 },
        }}
      >
        <Typography
          ref={sectionHeader}
          sx={{
            color: 'other.spaceCadet',
            mb: { xs: 2, md: 5 },
            px: { xs: 2, md: 0 },
          }}
          variant="h2"
        >
          Facts and Figures
        </Typography>

        <TransactionStatusSnapshotFilters />
        <TransactionStatusSnapshotModal />
        <TransactionStatusSnapshotChips />

        <Paper elevation={0}>
          <Stack
            sx={{
              alignItems: { xs: 'center', md: 'start' },
              flexDirection: { xs: 'column', md: 'row' },
              flexWrap: 'wrap',
              gap: 3,
              justifyContent: {
                xs: 'center',
                md: 'space-evenly',
              },
              minHeight: 444,
              pb: 3,
              mt: 3,
              pt: { xs: 2, md: 3 },
            }}
          >
            <PieChart
              data={data.listings}
              isLoading={isLoading}
              isMobile={isMobile}
              interactionType={InteractionType.listing}
            />

            <PieChart
              data={data.offers}
              isLoading={isLoading}
              isMobile={isMobile}
              interactionType={InteractionType.offer}
            />

            <Show when={hasLeasesAccess}>
              <PieChart
                data={data.leases}
                isLoading={isLoading}
                isMobile={isMobile}
                interactionType={InteractionType.lease}
              />
            </Show>
          </Stack>

          <Typography
            component="p"
            sx={{
              color: 'text.secondary',
              paddingBottom: { xs: 0.5, md: 1.5 },
              paddingLeft: { xs: 2, md: undefined },
              paddingRight: { xs: 2, md: undefined },
              textAlign: { xs: 'start', md: 'end' },
              width: '100%',
            }}
            variant="body3"
          >
            Statuses hidden: Closed, Expired, Lost, Rejected, Withdrawn, and
            Canceled
          </Typography>
        </Paper>
      </SectionContainer>
    </FactsAndFiguresContext.Provider>
  );
}
